// covered-by: frontends/carrie/src/ask-v2/rail/__tests__/AskV2RightRail.test.tsx

import { CaretDown } from "@phosphor-icons/react";
import { Link as RouterLink } from "@tanstack/react-router";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { useWordClamp } from "./useWordClamp";

import { MemoryChipStatic } from "../../components/memory-citation/MemoryChipFace";
import {
  clipAtWord,
  memoryMeta,
} from "../../components/memory-citation/memoryLabels";
import {
  MEMORY_LINK_TO,
  memoryTabSearch,
} from "../../components/memory-citation/memoryLink";
import { type MemoryCitation } from "../../utils/memoryCitationFields";
import {
  memoryFocusKey,
  useMemoryFocus,
  useMemoryLitHandlers,
} from "../../utils/memoryCitationFocus";

/** What a screen reader reads of a collapsed card whose clamp cannot be measured. */
const DESCRIPTION_CHARS = 120;
/** The card text's line height; the clamp counts lines in these. Keep it equal to `leading-[19px]` below. */
const TEXT_LINE_HEIGHT_PX = 19;
const TEXT_LINES = 2;
const TEXT_CLASS =
  "whitespace-pre-line text-[13px] leading-[19px] text-puntt-neutral-gray-12";

/**
 * One saved memory the answer uses, in the rail.
 *
 * The same visual language as the Cited claims cards: a numbered chip, the text,
 * a quiet meta line. The chip is the one in the answer (`M1`).
 *
 * Hovering or focusing the card tints the sentence the memory supports in the
 * answer, the way hovering the chip does. Click opens the whole text.
 *
 * why no copy of that sentence on the card, and no button to scroll to it: the
 * tint already shows which sentence the memory rests on, and a quote of it under
 * every card only repeated the answer.
 *
 * why a grid: the chip and the text are two columns, and the meta and the links
 * below sit in the TEXT column whatever the chip's width ("M10" is wider than
 * "M1"). A fixed left padding matched M1 only. The toggle button is a
 * subgrid of the card, so it shares the columns and still holds chip, text and
 * caret as one control.
 *
 * why the clamp is measured and not CSS alone: `line-clamp-2` ends the last line
 * where the line ends, and Chrome does that mid-word ("Magnesiu..."). The text
 * shown is the longest run of whole words that fits two lines (`useWordClamp`);
 * the CSS clamp stays as the net where nothing can be measured.
 *
 * A memory whose words are only warhol's 120-character edge label (`clipped`) is
 * shown as cut, with no toggle that would reveal nothing more, and links to
 * Memories straight away: the whole memory is there.
 */
export function AskV2RailMemoryCard({
  memory,
  number,
  scope,
}: {
  memory: MemoryCitation;
  number: number;
  /** The message the memory is cited in: with the memory id, the link to its chip. */
  scope: string;
}) {
  const { t, i18n } = useTranslation("ui");
  const [expanded, setExpanded] = useState(false);
  const key = memoryFocusKey(scope, memory.id);
  const { lit } = useMemoryFocus(key);
  const litHandlers = useMemoryLitHandlers(key);
  const meta = memoryMeta(memory, t, i18n.language);
  const id = memory.id;
  const prefix = `ask-v2-rail-memory-card-${id}`;
  const textId = useId();
  const descriptionId = useId();
  const dated = [meta.type, meta.date].filter(Boolean);
  const whole = memory.clipped !== true;
  // A card with nothing more to reveal shows its note and its link at once.
  const open = expanded || !whole;
  const { probeRef, clipped } = useWordClamp(
    memory.text,
    TEXT_LINES,
    TEXT_LINE_HEIGHT_PX,
    whole && !expanded,
  );
  // why not the visible text while collapsed: where nothing can be measured it holds the whole memory under a CSS clamp, and a description is read in full, clamp or not.
  const collapsedDescription =
    clipped ?? clipAtWord(memory.text, DESCRIPTION_CHARS);
  // why a short name and a description: the toggle's own text is the whole memory, which a screen reader would read as the button's name.
  const toggleName = meta.type
    ? t("askPuntt.memoryCitation.chipAriaWithType", {
        n: number,
        type: meta.type,
      })
    : t("askPuntt.memoryCitation.chipAria", { n: number });
  const header = (
    <>
      <MemoryChipStatic
        number={number}
        testId={`${prefix}-number`}
        className="mt-px min-w-11"
      />
      <span className="relative block min-w-0">
        <span
          id={textId}
          data-testid={`${prefix}-text`}
          className={`block ${TEXT_CLASS} ${
            whole && !expanded ? "line-clamp-2" : ""
          }`}
        >
          {whole && !expanded && clipped ? clipped : memory.text}
        </span>
        {/* The probe the clamp measures in: same type, no layout, no reader. */}
        <span
          ref={probeRef}
          aria-hidden="true"
          data-testid={`${prefix}-text-probe`}
          className={`pointer-events-none invisible absolute inset-x-0 top-0 ${TEXT_CLASS}`}
        />
      </span>
    </>
  );

  return (
    <li
      {...litHandlers}
      data-testid={prefix}
      data-lit={lit ? "true" : "false"}
      // why a subgrid of the LIST: a card's own chip track was as wide as its own chip, so M9 and M10 put their text at different x; the list owns the tracks and fits the widest chip.
      className="puntt-memory-card col-span-3 grid grid-cols-subgrid rounded-lg border border-puntt-neutral-gray-5 bg-base-white"
    >
      {whole ? (
        <button
          type="button"
          onClick={() => setExpanded((current) => !current)}
          aria-expanded={expanded}
          aria-label={toggleName}
          aria-describedby={expanded ? textId : descriptionId}
          data-testid={`${prefix}-toggle`}
          // why bg-transparent: preflight is off, so a bare <button> paints the UA grey over the card.
          className="col-span-3 grid grid-cols-subgrid items-start rounded-lg bg-transparent px-3 pb-1 pt-2.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--puntt-link-memory)]"
        >
          {header}
          {expanded ? null : (
            <span
              id={descriptionId}
              hidden
              data-testid={`${prefix}-description`}
            >
              {collapsedDescription}
            </span>
          )}
          <CaretDown
            size={14}
            aria-hidden="true"
            className={`mt-1 shrink-0 text-puntt-neutral-gray-11 transition-transform ${
              expanded ? "" : "-rotate-90"
            }`}
          />
        </button>
      ) : (
        <div className="col-span-3 grid grid-cols-subgrid items-start px-3 pb-1 pt-2.5">
          {header}
        </div>
      )}
      <div
        data-testid={`${prefix}-body`}
        className="col-span-2 col-start-2 min-w-0 pb-2.5 pr-3"
      >
        {dated.length > 0 || meta.origin ? (
          <p
            data-testid={`${prefix}-meta`}
            className="text-[12px] leading-4 text-puntt-neutral-gray-11"
          >
            {dated.length > 0 ? (
              <>
                {meta.type ? (
                  <span className="font-semibold text-puntt-neutral-gray-11">
                    {meta.type}
                  </span>
                ) : null}
                {meta.type && meta.date ? " · " : null}
                {meta.date}
              </>
            ) : null}
            {meta.origin ? (
              <span className="block" data-testid={`${prefix}-origin`}>
                {meta.origin}
              </span>
            ) : null}
          </p>
        ) : null}
        {open ? (
          <>
            <p
              data-testid={`${prefix}-note`}
              className="mt-2 text-[12px] leading-4 text-puntt-neutral-gray-11"
            >
              {t("askPuntt.memoryCitation.savedNote")}
            </p>
            <RouterLink
              to={MEMORY_LINK_TO}
              search={memoryTabSearch(memory.text)}
              data-testid={`${prefix}-open-in-memories`}
              className="mt-2 block w-fit text-[12px] font-semibold leading-4 text-[color:var(--puntt-link-memory)] no-underline hover:underline"
            >
              {t("askPuntt.memoryCitation.openInMemories")}
            </RouterLink>
          </>
        ) : null}
      </div>
    </li>
  );
}
